import { defineStore } from 'pinia'
import { ref, computed } from 'vue'
import * as authApi from '@core/api/modules/auth'
import type { BackendUser } from '@core/api/modules/auth'
import { setToken, clearToken, getToken } from '@core/api'
import { DEPT_PERMISSIONS } from '@core/config/menu'

interface DeptProfile {
  key: string
  labelKey: string
  permissions: string[]
}

/** Map department keys to i18n label keys */
export const DEPT_PROFILES: DeptProfile[] = [
  { key: 'admin', labelKey: 'roles.admin', permissions: [] },
  { key: 'qc', labelKey: 'roles.qc', permissions: [DEPT_PERMISSIONS.QC] },
  { key: 'external', labelKey: 'roles.external', permissions: [DEPT_PERMISSIONS.EXTERNAL] },
  { key: 'hr', labelKey: 'roles.hr', permissions: [DEPT_PERMISSIONS.HR] },
  { key: 'system', labelKey: 'roles.system', permissions: [DEPT_PERMISSIONS.SYSTEM] },
  { key: 'warehouse', labelKey: 'roles.warehouse', permissions: [DEPT_PERMISSIONS.WAREHOUSE] },
  { key: 'production', labelKey: 'roles.production', permissions: [DEPT_PERMISSIONS.PRODUCTION] },
  { key: 'equipment', labelKey: 'roles.equipment', permissions: [DEPT_PERMISSIONS.EQUIPMENT] },
]

export const useUserStore = defineStore('user', () => {
  const currentUser = ref<BackendUser | null>(null)
  const isLoggedIn = ref(false)

  /**
   * Admin "view as" profile.
   * null = full admin view; a department key = restricted view.
   */
  const activeProfile = ref<string | null>(null)

  async function login(account: string, password: string, captcha: string, captchaId: string) {
    const res = await authApi.login(account, password, captcha, captchaId)

    if (res.code === 200 && res.data) {
      const { token, user } = res.data
      if (!token || !user) {
        return { success: false, message: res.message || '登录响应异常，请重试' }
      }
      setToken(token)
      currentUser.value = user
      isLoggedIn.value = true
      activeProfile.value = null
      localStorage.setItem('currentUser', JSON.stringify(user))
      return { success: true }
    }

    return { success: false, message: res.message || '用户名或密码错误' }
  }

  function logout() {
    currentUser.value = null
    isLoggedIn.value = false
    activeProfile.value = null
    clearToken()
    localStorage.removeItem('currentUser')
  }

  function initUser() {
    const token = getToken()
    const saved = localStorage.getItem('currentUser')
    if (token && saved) {
      try {
        currentUser.value = JSON.parse(saved)
        isLoggedIn.value = true
      } catch {
        logout()
      }
    } else {
      logout()
    }
  }

  /** Whether the real account is an admin */
  const isAdmin = computed(() => currentUser.value?.isAdmin === 1)

  /** Switch admin view to a specific department (or null for full admin) */
  function setActiveProfile(profileKey: string | null) {
    activeProfile.value = profileKey
  }

  function hasPermission(permission: string): boolean {
    if (!currentUser.value) return false
    // No permission required
    if (!permission) return true

    // Admin with "view as" department active
    if (isAdmin.value && activeProfile.value && activeProfile.value !== 'admin') {
      const profile = DEPT_PROFILES.find(p => p.key === activeProfile.value)
      if (profile) {
        return profile.permissions.includes(permission)
      }
    }

    // Admin with full view
    if (isAdmin.value) return true

    // Normal department user — check interfaceDic
    return currentUser.value.interfaceDic?.includes(permission) || false
  }

  return {
    currentUser,
    isLoggedIn,
    isAdmin,
    activeProfile,
    login,
    logout,
    initUser,
    hasPermission,
    setActiveProfile,
  }
})
